import { Injectable } from '@angular/core';
import 'rxjs/add/operator/toPromise';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { ModalController} from 'ionic-angular';
import { AppData } from '../providers/app-data';
import { EsriObjectData } from '../providers/esri-object-data';
import { LayerData } from '../providers/layer-data';
import { Utilities } from '../providers/utilities';
import { XandY, Sublayer } from '../interfaces';

@Injectable()
export class IdentifyData {

    public selectedFeatures: Array<any>;
    selectedIndex: number = 0;
    identifyModal: any;
    public identifyComponent: any;

    constructor(private http: HttpClient, public modalCtrl: ModalController,
        private appData: AppData, private esriObjectData: EsriObjectData, private layerData: LayerData, private utilities: Utilities) {
    }

    presentModal() {
        if (!this.identifyModal) {
            this.identifyModal = this.modalCtrl.create(this.identifyComponent);
            this.identifyModal.present();
        }
    }

    dismissModal() {
        if (this.identifyModal) {
            this.identifyModal.dismiss().then(() => {
                this.identifyModal = null;
                this.utilities.removeModalClassFromBody();
            });
        } else {
            this.utilities.removeModalClassFromBody();
        }
    }

	executeIdentifyTask(identifyParams, identifyTask): Observable<any> {

		this.selectedFeatures = null;
        this.appData.selectedFeature = null;
        const options = {
            headers: new HttpHeaders({
                'Content-Type':  'application/json',
                'Access-Control-Allow-Credentials': 'true'
            })
        };

        let geometry:XandY = {x: identifyParams.event.mapPoint.x, y:identifyParams.event.mapPoint.y};

        let formParams = new FormData();

        formParams.append('f', 'json');
        formParams.append('tolerance', '8');
        formParams.append('geometryType', 'esriGeometryPoint');
        formParams.append('sr', '102100');
        formParams.append('geometry', JSON.stringify(geometry));
        formParams.append('returnGeometry', 'true');
        formParams.append('imageDisplay', '1664,426,96');//TODO, what is this?
        formParams.append('layers', identifyParams.layerIds.join(','));//TODO
        let sublayers = this.layerData.getSublayers();
        let identifyLayers: Array<Sublayer>= [];

        identifyParams.layerIds.forEach((index)=> {
            let sublayer = sublayers[index];
            sublayer.source.type = 'dataLayer';
            sublayer.source.dataSource.type = 'queryTable';
            var dsGeometryType = sublayer.source.dataSource.geometryType;
            sublayer.source.dataSource.geometryType = this.esriObjectData.getEsriGeometryString(dsGeometryType);
            identifyLayers.push(sublayer);
        });

        let dynamicLayersJson = JSON.stringify(identifyLayers);
        formParams.append('dynamicLayers', dynamicLayersJson);

        let extentArray:Array<number> = [];
        let extent = identifyParams.extent;
        extentArray.push(extent.xmin);
        extentArray.push(extent.ymin);
        extentArray.push(extent.xmax);
        extentArray.push(extent.ymax);
        formParams.append('mapExtent', extentArray.join(','));
        return this.http.post(this.appData.settings.mapServerUrl + '/identify',
        formParams, options).map(response => {
            this.setSelectedFeaturesFromIdentify(response);
       });

    }

    async executeHitTest(mapView: any, evt:any): Promise<any> {
        this.selectedFeatures = [];
        let originalX = evt.x;
        let originalY = evt.y;
        let factor = this.appData.settings.hitTestFactor;
        let gap = 3;
        let responses = [];
        for (let xIndex = 0 - factor; xIndex <= factor; xIndex++) {
            for (let yindex = 0 - factor; yindex <= factor; yindex++) {
                evt.x = originalX + (xIndex * gap);
                evt.y = originalY + (yindex * gap);
                await this.hitTest(mapView, evt, responses);
            }
        }
        return this.setSelectedFeaturesFromHitResults(responses);
    }

    hitTest(mapView: any, evt:any, previousResponses): Promise<any> {
        return mapView.hitTest(evt).then((response) => {
            previousResponses.push(response);
            return response;
        });
    }

	setSelectedFeaturesFromIdentify(response) {
        let jsonRresponse = JSON.parse(response._body);
        let collection = jsonRresponse.results.map((result) => {
            return this.getFeatureFromIdentifyResult(result);
        });
        this.setSelectedFeaturesCommon(collection);        
    }

    setSelectedFeaturesFromHitResults(responses) {
        let graphics = [];
        responses.forEach((response) => {
            response.results.forEach((result) => {
                // per 12c, OBJECTID column was added to spatial layer, so filtering on OBJECTID as well.
                let previouslyAdded = graphics.filter((g)=> {return g.uid === result.graphic.uid ||
                     (result.graphic.attributes && g.attributes && result.graphic.attributes['OBJECTID'] === g.attributes['OBJECTID']); });
                if (previouslyAdded.length === 0) {
                    graphics.push(result.graphic);
                }
            });
        });
        let collection = graphics.filter((graphic) => {
            return graphic.layer != null && graphic.attributes;
        }).map((graphic) => {
            return this.getFeatureFromGraphic(graphic);
        });
        var hpmsLayerCollection = collection.filter((obj) => obj.layerTitle === 'HPMS Sample');
        var newCollection = [];
        // if more than one hpms sample for the same HPMS Sample Number then include only one for identify purposes.
        // Duplicate Layers show up when user searches for a HPMS Sample - this creates a "filtered HPMS layer" with the searched ID
        // Related to TFS #12464
        if (hpmsLayerCollection.length > 1){
            collection.forEach((item, index) => {
                // In the new collection, remove duplicates.
                if (newCollection.findIndex(i => i.attributes[3].value === item.attributes[3].value) === -1){
                    newCollection.push(item);
                }
            });
            this.setSelectedFeaturesCommon(newCollection);
        } else {
            this.setSelectedFeaturesCommon(collection);
        }
    }

    setSelectedFeaturesCommon(collection: Array<any>) {
        this.selectedFeatures = collection;
        this.selectFirstFeature();
    }

    getFeatureFromIdentifyResult(result) {
        let feature: any = {};
        feature.layerId = result.layerId;
        feature.layerTitle = result.layerName;
        var fields = this.appData.DynamicSubLayers[feature.layerId].displayFields;
        var attributes = [];
        for (var attr in result.attributes) {
            if (fields.indexOf(attr) >= 0) {
                let newFieldValue = attr.split('_').join(' ');
                attributes.push({ "field": attr, "alias": newFieldValue, "value": result.attributes[attr] });
            }
        }
        feature.attributes = attributes;
        result.geometry.geometryType = result.geometryType;
        feature.geometry = this.esriObjectData.getGeometry(result.geometry);
        return feature;
    }

    getFeatureFromGraphic(graphic) {
        let feature = {'attributes':[], 'geometry':{}, 'layerTitle': ''};
        let matchingLayers = this.appData.CombinedFeatureLayers.filter((x)=> { return x.id === graphic.layer.id});
        let matchingLayer;
        let aliasDictionary;        
        if (matchingLayers.length> 0) {
            matchingLayer = matchingLayers[0];
            feature.layerTitle = matchingLayer.title;
            aliasDictionary = this.appData.getFieldAliasDictionary(matchingLayer);
        }
        
        for (var attr in graphic.attributes) {
            let newFieldValue = attr.split('_').join(' ');
            let newAttribute = { "field": attr, "alias": newFieldValue, "value": graphic.attributes[attr] };            
            // Some field values are modified slightly from what is stored in the database            
            newAttribute.value = this.appData.getEnhancedValue(matchingLayer, attr, newAttribute.value);
            
            if (aliasDictionary && aliasDictionary[attr]) {
                newAttribute.alias = aliasDictionary[attr];
                feature.attributes.push(newAttribute);
            }            
        }
        graphic.geometry.geometryType = graphic.layer.geometryType;
        feature.geometry = this.esriObjectData.getGeometry(graphic.geometry);
        return feature;
    }

    selectFirstFeature(): void {
        if (this.selectedFeatures.length > 0) {
			this.selectFeatureByIndex(0);
        } else {
            this.appData.message = "No features found";
        }
    }

    selectFeatureByIndex(index:number) {
        this.selectedIndex = index;
        this.appData.selectedFeature = this.selectedFeatures[this.selectedIndex];
    }

}


// WEBPACK FOOTER //
// ./src/providers/identify-data.ts